/* eslint-disable react/jsx-no-undef */
import classNames from 'classnames';
import { MappingConnector } from './MappingConnector';
import {
  getDropNodeFromXY,
  MAPPING_ROW_HEIGHT,
  getRowHoveredPropsForConnectors,
  getDraggingProps,
} from './MappingConnectorUtils';
import { DraggingMappingConnector } from './DraggingMappingConnector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RowHoveredMappingConnector } from './RowHoveredMappingConnector';
import { useResizeObserver } from 'hooks/useResizeObserver';
import {
  useMappingEditorStore,
  getFlowScrollPosition,
  getFlowHoveredNode,
  getStepHoveredNode,
  getStepScrollPosition,
  getForInput,
  getDraggingFrom,
  getDragTrulyStarted,
  MappingPanelType,
  storeIdSelector,
  getDragTargetRowHeights,
  getEndRowHeights,
} from '../MappingEditorStore';
import { MappingConnectorGradient } from './MappingConnectorGradient';

export interface IMappingConnectorsProps {
  className?: string;
  visibleStepNodes: string[];
  visibleFlowDataNodes: string[];
}

const MAPPING_CONNECTORS_CLASSNAME = 'dp-mapping-connectors';
export const CONNECTOR_AREA_OVERLAP = 15;
export const LEFT_COLUMN_EDGE = -1;
export const RIGHT_COLUMN_EDGE = 116.5;
const defaultDragCoords = { x: 0, y: 0 };

/**
 * Connector lines between mappings
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function MappingConnectors({ className, visibleStepNodes, visibleFlowDataNodes }: IMappingConnectorsProps) {
  // TODO could make these update more atomically if we move hovered stuff into children that care about hover.
  const hoveredFlowNodeId = useMappingEditorStore(getFlowHoveredNode);
  const id = useMappingEditorStore(storeIdSelector);
  const hoveredStepNodeId = useMappingEditorStore(getStepHoveredNode);
  const draggingFrom = useMappingEditorStore(getDraggingFrom);
  const dragHasStarted = useMappingEditorStore(getDragTrulyStarted);
  const stepScroll = useMappingEditorStore(getStepScrollPosition);
  const flowScroll = useMappingEditorStore(getFlowScrollPosition);
  const dragTargetRowHeights = useMappingEditorStore(getDragTargetRowHeights);
  const endRowHeights = useMappingEditorStore(getEndRowHeights);

  const mappings = useMappingEditorStore(state => state.mappings);
  const hoverNode = useMappingEditorStore(state => state.hoverNode);
  const hoverClear = useMappingEditorStore(state => state.hoverClear);
  const setDragHasStarted = useMappingEditorStore(state => state.setDragHasStarted);

  const clipPathId = `mapping-connector-clip-${id}`;
  const clipPath = `url(#${clipPathId})`;
  const forInput = useMappingEditorStore(getForInput);
  const [dragCoords, setDragCoords] = useState(defaultDragCoords);
  const [setNode, observerEntry] = useResizeObserver();
  const dragMove = useRef(false);

  const dragging = getDraggingProps(
    visibleFlowDataNodes,
    visibleStepNodes,
    stepScroll,
    flowScroll,
    hoveredStepNodeId,
    hoveredFlowNodeId,
    draggingFrom,
    forInput,
    endRowHeights
  );
  const isDragging = !!dragging;
  const observedRight = observerEntry?.contentRect.right || 0;
  const observedHeight = observerEntry?.contentRect.height || '100%';
  const [clipPathHeight, setClipPathHeight] = useState<string | number>('100%');
  const rightEdge = Math.max(observedRight - LEFT_COLUMN_EDGE, RIGHT_COLUMN_EDGE);

  useEffect(() => {
    setTimeout(() => {
      if (typeof observedHeight !== 'number') {
        return;
      }
      const height = observedHeight;
      clipPathHeight !== height && setClipPathHeight(height);
    });
  }, [clipPathHeight, observedHeight]);

  const rowHoveredProps = !dragging
    ? getRowHoveredPropsForConnectors(
        visibleStepNodes,
        visibleFlowDataNodes,
        hoveredFlowNodeId,
        hoveredStepNodeId,
        flowScroll,
        stepScroll,
        mappings,
        forInput
      )
    : undefined;

  const getDragCoords = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const dragEventTarget = e.currentTarget;
      if (!dragMove.current) {
        // request animation frame to limit how often this fires:
        window.requestAnimationFrame(() => {
          dragMove.current = false;
          const containerRect = dragEventTarget.getBoundingClientRect();
          let x = e.clientX - containerRect.x;
          x = Math.min(Math.max(LEFT_COLUMN_EDGE, x), rightEdge);
          // adjust Y for the column headers:
          let y = e.clientY - containerRect.y - MAPPING_ROW_HEIGHT;
          setDragCoords({ x, y });
        });

        dragMove.current = true;
        return;
      }
    },
    [rightEdge]
  );

  const onMouseDrag = dragging ? getDragCoords : undefined;

  const handleHoverByXY = useCallback(
    (hitPath: string, side: MappingPanelType) => {
      // don't worry about this optimization till we know we need it, as it causes a false negative
      // if (hitPath !== prevHitPath.current) {
      // prevHitPath.current = hitPath;
      if (hitPath) {
        hoverNode(hitPath, side);
      } else {
        hoverClear();
      }
      // }
    },
    [hoverClear, hoverNode]
  );

  useEffect(() => {
    // reset these when dragging stops
    if (!isDragging) {
      setDragCoords(defaultDragCoords);
    }
  }, [isDragging]);

  useEffect(() => {
    let hitPath = '';
    let hoverSide = MappingPanelType.STEP;
    if (!dragHasStarted || !dragging) {
      // bail out if not dragging
      return;
    } else {
      hoverSide = dragging.targetSide;
      hitPath = getDropNodeFromXY(
        dragCoords.x,
        dragCoords.y,
        dragging.targetRows,
        dragTargetRowHeights,
        dragging.leftToRight,
        rightEdge,
        dragging.targetScroll
      );
    }
    handleHoverByXY(hitPath, hoverSide);
    // if callback internals aren't memoized, we only want to fire this for x/y changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragCoords.x, dragCoords.y]);

  useEffect(() => {
    if ((dragCoords.x > 0 || dragCoords.y > 0) && !dragHasStarted) {
      // will this throw an error if it's in an effect instead?
      //Call DRAG_CONNECTION_STARTED once we started drawing line.
      setDragHasStarted();
    }
  }, [dragCoords.x, dragCoords.y, dragHasStarted, setDragHasStarted]);

  return (
    <section className={getClassName(className)} onMouseMove={onMouseDrag} ref={setNode}>
      <svg width="100%" height="100%" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
        <clipPath id={clipPathId} height={observedHeight} width="100%">
          <rect x={-CONNECTOR_AREA_OVERLAP} width={rightEdge + 2 * CONNECTOR_AREA_OVERLAP} height={observedHeight} />
        </clipPath>
        {mappings.map(prop => (
          <MappingConnector
            key={prop.nodeName}
            {...prop}
            startIndex={prop.startIndex}
            endIndex={prop.endIndex}
            activeDrag={isDragging}
            rightEdge={rightEdge}
            clipPath={clipPath}
          />
        ))}
        {rowHoveredProps && (
          <RowHoveredMappingConnector {...rowHoveredProps} rightEdge={rightEdge} clipPath={clipPath} />
        )}
        {dragging && (
          <DraggingMappingConnector
            {...dragging}
            endX={dragCoords.x}
            endY={dragCoords.y}
            rightEdge={rightEdge}
            reallyDragging={dragHasStarted}
          />
        )}
        <defs>
          <MappingConnectorGradient
            id="left-right-fade"
            className={`${MAPPING_CONNECTORS_CLASSNAME}__normal-fade-stop`}
          />
          <MappingConnectorGradient
            id="left-right-fade-invalid"
            className={`${MAPPING_CONNECTORS_CLASSNAME}__invalid-fade-stop`}
          />
          <MappingConnectorGradient
            id="right-left-fade"
            className={`${MAPPING_CONNECTORS_CLASSNAME}__normal-fade-stop`}
            rightToLeft
          />
          <MappingConnectorGradient
            id="right-left-fade-invalid"
            className={`${MAPPING_CONNECTORS_CLASSNAME}__invalid-fade-stop`}
            rightToLeft
          />
        </defs>
      </svg>
    </section>
  );
}

function getClassName(className?: string) {
  return classNames(MAPPING_CONNECTORS_CLASSNAME, className, {});
}
